import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Popconfirm,
  Breadcrumb,
  Tag,
  Card,
  Row,
  Col,
  message,
  Alert,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
  AlertOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { pressureEquipmentService, PressureEquipment } from '@/renderer/services/pressureEquipmentService'
import './PressureEquipmentList.css'

const PRESSURE_EQUIPMENT_STATUSES = [
  { label: 'Available (AVLB)', value: 'AVLB', color: 'green' },
  { label: 'In Use (NUSE)', value: 'NUSE', color: 'blue' },
  { label: 'Inspection (INSP)', value: 'INSP', color: 'orange' },
  { label: 'Maintenance (MTCE)', value: 'MTCE', color: 'orange' },
  { label: 'Repair (REPR)', value: 'REPR', color: 'red' },
  { label: 'Quarantined (QRTD)', value: 'QRTD', color: 'red' },
  { label: 'Retired (RTRD)', value: 'RTRD', color: 'purple' },
]

const PRESSURE_EQUIPMENT_TYPES = [
  { label: 'BOP (Blowout Preventer)', value: 'bop' },
  { label: 'Choke Valve', value: 'choke' },
  { label: 'Pressure Control', value: 'pressure_control' },
  { label: 'Stripper Box', value: 'stripper' },
]

const COMPANY_LOCATIONS = [
  { label: 'Neuquén - Operaciones', value: 'Neuquén - Operaciones' },
  { label: 'Neuquén - Base', value: 'Neuquén - Base' },
  { label: 'Comodoro - Operaciones', value: 'Comodoro - Operaciones' },
  { label: 'Comodoro - Base', value: 'Comodoro - Base' },
  { label: 'Buenos Aires - Office', value: 'Buenos Aires - Office' },
]

interface ExtendedPressureEquipment extends PressureEquipment {
  key: string
  testStatusWarning?: string
}

export function PressureEquipmentList() {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [equipment, setEquipment] = useState<ExtendedPressureEquipment[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState<ExtendedPressureEquipment | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })
  const [filters, setFilters] = useState({ status: '', location: '', equipment_type: '' })

  useEffect(() => {
    loadEquipment()
  }, [pagination.current, filters])

  const loadEquipment = async () => {
    setLoading(true)
    try {
      const skip = (pagination.current - 1) * pagination.pageSize
      const response = await pressureEquipmentService.listPressureEquipment(
        skip,
        pagination.pageSize,
        filters
      )
      const equipmentWithKeys: ExtendedPressureEquipment[] = response.items.map((item) => ({
        ...item,
        key: item.id,
        testStatusWarning: getTestStatusWarning(item),
      }))
      setEquipment(equipmentWithKeys)
      setPagination({ ...pagination, total: response.total })
    } catch (error) {
      message.error('Failed to load pressure equipment')
    } finally {
      setLoading(false)
    }
  }

  const getTestStatusWarning = (item: PressureEquipment): string | undefined => {
    if (!item.next_test_due) return undefined

    const nextTestDate = dayjs(item.next_test_due)
    const today = dayjs()
    const daysUntilTest = nextTestDate.diff(today, 'days')

    if (daysUntilTest < 0) return 'OVERDUE'
    if (daysUntilTest <= 7) return 'DUE SOON'
    return undefined
  }

  const handleAdd = () => {
    form.resetFields()
    setEditingEquipment(null)
    setIsModalVisible(true)
  }

  const handleEdit = (item: ExtendedPressureEquipment) => {
    form.setFieldsValue({
      ...item,
      last_test_date: item.last_test_date ? dayjs(item.last_test_date) : undefined,
    })
    setEditingEquipment(item)
    setIsModalVisible(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      const dataToSend = {
        ...values,
        last_test_date: values.last_test_date ? values.last_test_date.format('YYYY-MM-DD') : undefined,
      }

      if (editingEquipment) {
        await pressureEquipmentService.updatePressureEquipment(editingEquipment.id, dataToSend)
        message.success('Equipment updated successfully')
      } else {
        await pressureEquipmentService.createPressureEquipment(dataToSend)
        message.success('Equipment created successfully')
      }
      setIsModalVisible(false)
      setPagination({ ...pagination, current: 1 })
      await loadEquipment()
    } catch (error) {
      console.error('Error saving equipment:', error)
      message.error('Failed to save equipment')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await pressureEquipmentService.deletePressureEquipment(id)
      message.success('Equipment deleted successfully')
      await loadEquipment()
    } catch (error) {
      console.error('Error deleting equipment:', error)
      message.error('Failed to delete equipment')
    }
  }

  const columns: ColumnsType<ExtendedPressureEquipment> = [
    {
      title: 'Equipment Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      width: 140,
    },
    {
      title: 'Serial Number',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 130,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = PRESSURE_EQUIPMENT_STATUSES.find((s) => s.value === status)
        return statusConfig ? <Tag color={statusConfig.color}>{statusConfig.label}</Tag> : status
      },
    },
    {
      title: 'Next Test Due',
      dataIndex: 'next_test_due',
      key: 'next_test_due',
      width: 130,
      render: (date: string, record: ExtendedPressureEquipment) => {
        let color = 'green'
        let icon = null

        if (record.testStatusWarning === 'OVERDUE') {
          color = 'red'
          icon = <AlertOutlined style={{ marginRight: '4px' }} />
        } else if (record.testStatusWarning === 'DUE SOON') {
          color = 'orange'
          icon = <AlertOutlined style={{ marginRight: '4px' }} />
        }

        return date ? (
          <Tag color={color}>
            {icon}
            {dayjs(date).format('MMM DD, YYYY')}
          </Tag>
        ) : (
          '-'
        )
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 140,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Delete Equipment"
            description="Are you sure you want to delete this equipment?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const overdueEquipment = equipment.filter((e) => e.testStatusWarning === 'OVERDUE').length
  const dueSoonEquipment = equipment.filter((e) => e.testStatusWarning === 'DUE SOON').length

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <a onClick={() => navigate('/inventory')}>
            <HomeOutlined />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Inventory</Breadcrumb.Item>
        <Breadcrumb.Item>Pressure Equipment</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600 }}>
          Pressure Equipment Management
        </h1>
        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
          Manage BOP, chokes, and other pressure control equipment with test tracking
        </p>
      </div>

      {overdueEquipment > 0 && (
        <Alert
          message={`${overdueEquipment} equipment overdue for testing`}
          type="error"
          icon={<AlertOutlined />}
          style={{ marginBottom: '16px' }}
          showIcon
        />
      )}

      {dueSoonEquipment > 0 && (
        <Alert
          message={`${dueSoonEquipment} equipment due for testing soon`}
          type="warning"
          icon={<AlertOutlined />}
          style={{ marginBottom: '16px' }}
          showIcon
        />
      )}

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space wrap>
              <Select
                placeholder="Filter by Status"
                style={{ width: 180 }}
                allowClear
                options={PRESSURE_EQUIPMENT_STATUSES}
                onChange={(value) => {
                  setFilters({ ...filters, status: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Location"
                style={{ width: 180 }}
                allowClear
                options={COMPANY_LOCATIONS}
                onChange={(value) => {
                  setFilters({ ...filters, location: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Equipment Type"
                style={{ width: 180 }}
                allowClear
                options={PRESSURE_EQUIPMENT_TYPES}
                onChange={(value) => {
                  setFilters({ ...filters, equipment_type: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Equipment
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={equipment}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page) => setPagination({ ...pagination, current: page }),
        }}
        size="small"
      />

      <Modal
        title={editingEquipment ? 'Edit Equipment' : 'Add Equipment'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Equipment Type"
            name="equipment_type"
            rules={[{ required: true, message: 'Equipment Type is required' }]}
          >
            <Select placeholder="Select equipment type" options={PRESSURE_EQUIPMENT_TYPES} />
          </Form.Item>

          <Form.Item
            label="Serial Number"
            name="serial_number"
            rules={[{ required: true, message: 'Serial Number is required' }]}
          >
            <Input placeholder="Serial number" />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Status is required' }]}
          >
            <Select placeholder="Select status" options={PRESSURE_EQUIPMENT_STATUSES} />
          </Form.Item>

          <Form.Item
            label="Location"
            name="location"
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <Select placeholder="Select location" options={COMPANY_LOCATIONS} />
          </Form.Item>

          <Form.Item label="Max Pressure (PSI)" name="max_pressure">
            <InputNumber placeholder="Maximum pressure rating" />
          </Form.Item>

          <Form.Item label="Min Pressure (PSI)" name="min_pressure">
            <InputNumber placeholder="Minimum pressure rating" />
          </Form.Item>

          <Form.Item label="Test Pressure (PSI)" name="test_pressure">
            <InputNumber placeholder="Test pressure rating" />
          </Form.Item>

          <Form.Item label="Test Frequency (months)" name="test_frequency_months">
            <InputNumber placeholder="Test frequency in months" min={1} />
          </Form.Item>

          <Form.Item label="Last Test Date" name="last_test_date">
            <DatePicker />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
